import React, { useState, useRef } from 'react';
import { FiUploadCloud, FiDownload, FiFile, FiX, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';

const CsvUploader = ({ title, templateColumns, templateSampleRows, onUpload, onClose }) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const downloadTemplate = () => {
    const header = templateColumns.join(',');
    const rows = templateSampleRows.map(r => r.join(',')).join('\n');
    const csv = header + '\n' + rows;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_').toLowerCase()}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await onUpload(formData);
      setResult(res.data);
      toast.success(`Successfully imported ${res.data.inserted || res.data.count || 0} records`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Upload failed';
      const errors = err.response?.data?.errors;
      setResult({ error: msg, errors });
      toast.error(msg);
    }
    setUploading(false);
  };

  const s = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
    modal: { background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', position: 'relative' },
    close: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: 20 },
    dropzone: {
      border: `2px dashed ${dragging ? '#3b82f6' : '#e2e8f0'}`,
      borderRadius: 12, padding: '40px 20px', textAlign: 'center', cursor: 'pointer',
      background: dragging ? '#eff6ff' : file ? '#f0fdf4' : '#f8fafc',
      transition: 'all 0.2s', marginBottom: 20
    },
    btn: { padding: '12px 24px', borderRadius: 10, border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 14, fontFamily: 'Inter, sans-serif' },
    templateBtn: { background: '#f0f9ff', color: '#2563eb', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 10, border: '1px solid #bfdbfe', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    tag: { display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, marginRight: 4, marginBottom: 4 }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.close} onClick={onClose}><FiX /></button>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
          <FiUploadCloud style={{ marginRight: 8, verticalAlign: -3 }} />
          Bulk CSV Upload
        </h2>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>Upload a CSV file to import multiple {title.toLowerCase()} records at once.</p>

        {/* Template Download */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Required Columns:</span>
            <button style={s.templateBtn} onClick={downloadTemplate}>
              <FiDownload size={14} /> Download Template
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {templateColumns.map(col => (
              <span key={col} style={{ ...s.tag, background: '#f1f5f9', color: '#475569' }}>{col}</span>
            ))}
          </div>
        </div>

        {/* Drop Zone */}
        <div
          style={s.dropzone}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={e => handleFile(e.target.files[0])} />
          {file ? (
            <div>
              <FiFile size={32} color="#22c55e" style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>{file.name}</p>
              <p style={{ color: '#64748b', fontSize: 12 }}>{(file.size / 1024).toFixed(1)} KB</p>
              <button
                onClick={e => { e.stopPropagation(); setFile(null); setResult(null); }}
                style={{ marginTop: 8, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >Remove file</button>
            </div>
          ) : (
            <div>
              <FiUploadCloud size={40} color={dragging ? '#3b82f6' : '#94a3b8'} style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 600, color: '#334155', fontSize: 15 }}>Drag & drop your CSV here</p>
              <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>or click to browse files</p>
            </div>
          )}
        </div>

        {/* Upload Result */}
        {result && (
          <div style={{
            padding: 16, borderRadius: 10, marginBottom: 20, fontSize: 13,
            background: result.error ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${result.error ? '#fecaca' : '#bbf7d0'}`
          }}>
            {result.error ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>
                  <FiAlertCircle /> Upload Failed
                </div>
                <p style={{ color: '#991b1b' }}>{result.error}</p>
                {result.errors && result.errors.length > 0 && (
                  <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto' }}>
                    {result.errors.slice(0, 10).map((err, i) => (
                      <div key={i} style={{ color: '#b91c1c', fontSize: 12, marginTop: 2 }}>Row {err.row}: {err.message}</div>
                    ))}
                    {result.errors.length > 10 && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>...and {result.errors.length - 10} more errors</div>}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#16a34a' }}>
                <FiCheckCircle /> Successfully imported {result.inserted || result.count || 0} records
                {result.skipped > 0 && <span style={{ fontWeight: 400, color: '#ca8a04' }}> ({result.skipped} skipped)</span>}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button style={{ ...s.btn, background: '#f1f5f9', color: '#475569' }} onClick={onClose}>Cancel</button>
          <button
            style={{ ...s.btn, background: uploading || !file ? '#94a3b8' : '#2563eb', color: '#fff' }}
            disabled={uploading || !file}
            onClick={handleUpload}
          >
            {uploading ? 'Uploading...' : 'Upload & Import'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CsvUploader;
